import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { InputFieldset } from 'ui/inputFieldset';
import { SelectFieldset } from 'ui/selectFieldset';
import { SelectTriggerUI } from 'ui/selectTrigger';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  // Search,
  Plus,
  Edit2,
  Trash2,
  MapPin,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  X,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { AddZiyaratPayload, UpdateZiyaratPayload, Venue, Ziyarat } from 'types/ziyarat';
import useDebounce from 'hooks/useDebounce';
import { AddZiyarat, deleteZiyaratPlace, getVenuesList, getZiyaratList, updateZiyarat } from 'services/ziyaratService';
import { FullScreenLoading } from 'components/ui/loader';
import ConfirmDialog from 'ui/ConfirmDialog';
import Pagination from 'ui/pagination';
import { useScreenPermission } from 'hooks/useScreenPermission';
import { usePageHeader } from 'contexts/PageHeaderContext';


export const ZiyaratPlacesMasterPage = () => {
  const { canAdd, canEdit, canDelete, screenName } = useScreenPermission();
  const { setPageHeader } = usePageHeader();

  useEffect(() => {
    setPageHeader({
      title: screenName ?? "Manage Ziyarat",
      childTitle: "Manage ziyarat and their venue associations",
      icon: <MapPin className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [screenName, setPageHeader]);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Ziyarat | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ziyaratDeleteId, setZiyaratDeleteId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Ziyarat | null>(null);

  const [errors, setErrors] = useState({
    placeName: "",
    code: "",
    venueName: ""
  });

  const [editErrors, setEditErrors] = useState({
    code: "",
    placeName: "",
    venueName: ""
  });

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(false);
  const [ziyaratList, setZiyaratList] = useState<Ziyarat[]>([]);
  const [ziyarattotalCount, setZiyaratTotalCount] = useState(0);
  const [venuesOptions, setVenuesOptions] = useState<Venue[]>([]);

  // Filter states
  const [filters, setFilters] = useState({
    placeName: '',
    venue: '',
    status: '',
    pageNo: 1,
    pageSize: 20,
  });

  const mergedFilters = useMemo(
    () => ({
      ...filters,
      searchTerm,
    }),
    [filters, searchTerm],
  );

  const debouncedFilters = useDebounce(mergedFilters, 400);

  const [formData, setFormData] = useState({
    code: '',
    placeName: '',
    venueName: '',
    isActive: true,
  });

  const [editFormData, setEditFormData] = useState({
    publicId: '',
    code: '',
    placeName: '',
    venueName: '',
    isActive: true,
  });


  // API CALLING START
  const getVenuesListApi = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getVenuesList({ onlyActive: true });
      setVenuesOptions(data);
    } catch {
      toast.error("Failed to fetch venues");
    } finally {
      setLoading(false);
    }
  }, []);


  const fetchZiyaratList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getZiyaratList(debouncedFilters);
      setZiyaratList(data.items);
      setZiyaratTotalCount(data.totalCount);
    } catch {
      toast.error("Failed to fetch Ziyarat");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);


  useEffect(() => {
    getVenuesListApi();
    fetchZiyaratList();
  }, [
    getVenuesListApi,
    fetchZiyaratList,
  ]);
  // API CALLING END


  // Handle sorting
   const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };


  const sortedApplicants = useMemo(() => {
    if (!sortColumn) return ziyaratList;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...ziyaratList].sort((a: any, b: any) => {
      const aValue = String(a[sortColumn] ?? "");
      const bValue = String(b[sortColumn] ?? "");

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        { numeric: true, sensitivity: "base" }
      );

      return sortDirection === "asc"
        ? comparison
        : -comparison;
    });
  }, [ziyaratList, sortColumn, sortDirection]);



  const validateAddForm = () => {
    const newErrors = {
      placeName: "",
      code: "",
      venueName: ""
    };

    let isValid = true;

    if (!formData.placeName.trim()) {
      newErrors.placeName = "Name is required";
      isValid = false;
    }

    if (!formData.code.trim()) {
      newErrors.code = "Code is required";
      isValid = false;
    }

    if (!formData.venueName) {
      newErrors.venueName = "Venue is required";
      isValid = false;
    }

    if (ziyaratList.some(place => place.code.toLowerCase() === formData.code.toLowerCase())) {
      newErrors.code = "Code already exists. Please use a different code.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };


  const handleAdd = async () => {
    if (!validateAddForm()) return;
    try {
      const payload: AddZiyaratPayload = {
        name: formData.placeName,
        code: formData.code,
        venueId: Number(formData.venueName),
        isActive: formData.isActive
      };
      await AddZiyarat(payload);
      toast.success("Ziyarat place added successfully");
      setIsAddDialogOpen(false);
      resetForm();
      fetchZiyaratList(); // refresh list
    } catch (error) {
      toast.error("Failed to add ziyarat place");
    }
  };


  const handleEdit = (item: Ziyarat) => {
    setEditingItem(item);
    setEditFormData({
      publicId: item.publicId,
      code: item.code,
      placeName: item.name,   // map name → placeName
      venueName: String(item.venueId),
      isActive: item.isActive,
    });
    setEditErrors({
      code: "",
      placeName: "",
      venueName: ""
    });
    setIsEditDialogOpen(true);
  };


  const validateEditForm = () => {
    const newErrors = {
      code: "",
      placeName: "",
      venueName: ""
    };

    let isValid = true;

    if (!editFormData.placeName.trim()) {
      newErrors.placeName = "Name is required";
      isValid = false;
    }

    if (!editFormData.code.trim()) {
      newErrors.code = "Code is required";
      isValid = false;
    }

    if (!editFormData.venueName) {
      newErrors.venueName = "Venue is required";
      isValid = false;
    }

    // if (ziyaratList.some(place => place.code.toLowerCase() === editFormData.code.toLowerCase())) {
    //   newErrors.code = "Code already exists. Please use a different code.";
    //   isValid = false;
    // }
    setEditErrors(newErrors);
    return isValid;
  };


  const handleUpdate = async () => {
    if (!validateEditForm()) return;
    if (!editingItem) return;
    try {
      const payload: UpdateZiyaratPayload = {
        publicId: editingItem?.publicId ?? "",
        name: editFormData.placeName,
        code: editFormData.code,
        venueId: Number(editFormData.venueName),
        isActive: editFormData.isActive
      };
      await updateZiyarat(payload);
      toast.success("Ziyarat place updated successfully");
      fetchZiyaratList();
      setIsEditDialogOpen(false);
      setEditingItem(null);
    } catch (error) {
      toast.error("Failed to update ziyarat place");
    }
  };


  // STATUS CHANGE FUNCTIONALITY START
  const toggleStatus = (item: Ziyarat) => {
    setSelectedItem(item);
    setIsStatusDialogOpen(true);
  };

  const cancelStatusChange = () => {
    setSelectedItem(null);
    setIsStatusDialogOpen(false);
  };

  const confirmStatusChange = async () => {
    if (!selectedItem) return;
    try {
      const payload = {
        publicId: selectedItem.publicId,
        name: selectedItem.name,
        code: selectedItem.code,
        venueId: selectedItem.venueId,
        isActive: !selectedItem.isActive,
      };
      await updateZiyarat(payload);
      toast.success(
        `Ziyarat place ${selectedItem.isActive ? "deactivated" : "activated"} successfully`
      );
      fetchZiyaratList();
    } catch {
      toast.error("Failed to update status");
    } finally {
      setIsStatusDialogOpen(false);
    }
  };
  // STATUS CHANGE FUNCTIONALITY END


  // DELETE ZIYARAT FUNCTIONLITY START
  const handleDeleteZiyarat = (id: string) => {
    setZiyaratDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const cancelDeleteDraft = () => {
    setDeleteDialogOpen(false);
    setZiyaratDeleteId(null);
  };

  const confirmDeleteDraft = async () => {
    if (!ziyaratDeleteId) return;
    try {
      setLoading(true);
      await deleteZiyaratPlace(ziyaratDeleteId);
      toast.success("Ziyarat place deleted successfully");
      await fetchZiyaratList();
      setDeleteDialogOpen(false);
      setZiyaratDeleteId(null);
    } catch (error) {
      toast.error("Failed to delete");
    } finally {
      setLoading(false);
    }
  };
  // DELETE ZIYARAT FUNCTIONLITY END


  const resetForm = () => {
    setFormData({
      code: '',
      placeName: '',
      venueName: '',
      isActive: true,
    });
    setErrors({
      placeName: "",
      code: "",
      venueName: ""
    });
  };



  return (
    <div className="space-y-6 pb-8">
      {loading && <FullScreenLoading />}
      {canAdd && (
        <div className="flex justify-end">
          <Button
            onClick={() => {
              setIsAddDialogOpen(true);
              resetForm()
            }}
            style={{ background: '#5B9BD5' }}
            className="text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Ziyarat
          </Button>
        </div>
      )}

      {/* Search and Filters */}
      <Card className="border-[#5B9BD5]/30">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <Input
                placeholder="Search by place name, venue..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setFilters((prev) => ({ ...prev, pageNo: 1 }))
                }}
                className="pl-9 sm:pl-10 text-sm sm:text-base h-9 sm:h-10"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-500"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 w-full sm:w-10 h-9 sm:h-10 p-0"
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4 sm:mr-0" />
              <span className="sm:hidden ml-2">Advanced Filters</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters */}
      {showFilters && (
        <Card className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2 text-lg">
                  <Filter className="w-5 h-5" />
                  Advanced Filters
                </CardTitle>
                <CardDescription className="mt-1">
                  Filter ziyarat places by multiple criteria
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Place Name Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-place" className="text-sm">Place Name</Label>
                <Input
                  id="filter-place"
                  placeholder="Search by name..."
                  value={filters.placeName}
                  onChange={(e) => setFilters(prev => ({ ...prev, placeName: e.target.value, pageNo: 1 }))}
                  className="h-9 text-sm"
                />
              </div>

              {/* Venue Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-venue" className="text-sm">Venue</Label>
                <Select value={filters.venue}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, venue: value, pageNo: 1 }))}
                >
                  <SelectTrigger id="filter-venue" className="h-9 text-sm">
                    <SelectValue placeholder="All venues" />
                  </SelectTrigger>
                  <SelectContent>
                    {venuesOptions.map((venue) => (
                      <SelectItem key={venue.venueId} value={venue.venueId.toString()}>
                        {venue.venueName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-status" className="text-sm">Status</Label>
                <Select value={filters.status}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, status: value, pageNo: 1 }))}
                >
                  <SelectTrigger id="filter-status" className="h-9 text-sm">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active Only</SelectItem>
                    <SelectItem value="false">Inactive Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters Summary & Clear Button */}
            {(filters.placeName !== '' || filters.venue !== '' || filters.status !== '') && (
              <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#5B9BD5] mb-2">Active Filters:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {filters.placeName && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Place: {filters.placeName}
                      </Badge>
                    )}
                    {filters.venue && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                      >
                        Venue: {venuesOptions?.find((v) => String(v.venueId) === filters.venue)?.venueName}
                      </Badge>
                    )}
                    {filters.status && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Status: {filters.status === 'true' ? "Active" : "Inactive"}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ placeName: '', venue: '', status: '', pageNo: 1, pageSize: 5 })}
                  className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="bg-white border-[#5B9BD5]/30 ">
        <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50 rounded-t-xl">
          <CardTitle style={{ color: '#5B9BD5' }}>Ziyarat</CardTitle>
          <CardDescription>
            {ziyaratList.length} place{ziyaratList.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: '#E3F2FD' }}>
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('code')}
                  >
                    <div className="flex items-center gap-1">
                      Code
                      {sortColumn === 'code' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {sortColumn === 'name' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead
                    style={{ color: '#5B9BD5' }}
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    onClick={() => handleSort('venueName')}
                  >
                    <div className="flex items-center gap-1">
                      Venue Name
                      {sortColumn === 'venueName' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs text-center">IsActive ?</TableHead>
                  {(canEdit || canDelete) && (
                    <TableHead style={{ color: '#5B9BD5' }} className="px-4 py-3 text-xs text-right">Action</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {ziyaratList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 px-4 text-gray-500 text-sm">
                      No ziyarat places found
                    </TableCell>
                  </TableRow>
                ) : (
                  ziyaratList && sortedApplicants?.map((item, i) => (
                    <TableRow key={i} className="hover:bg-sky-50/30">
                      <TableCell className="px-4 py-3">
                        <span className="text-gray-900 text-sm font-mono font-medium">
                          {item.code}
                        </span>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-4 h-4 text-[#5B9BD5]" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">{item.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge variant="outline" className="bg-sky-50 border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                          {item.venueName}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center">
                          <Switch
                            checked={item.isActive}
                            onCheckedChange={() => toggleStatus(item)}
                            className="data-[state=checked]:bg-[#5B9BD5]"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(item)}
                              className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                              title="Edit"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteZiyarat(String(item.publicId))}
                              className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3 p-4">
            {ziyaratList.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No ziyarat places found</p>
              </div>
            ) : (
              ziyaratList && sortedApplicants?.map((item, i) => (
                <Card key={i} className="border-[#5B9BD5]/20 hover:border-[#5B9BD5]/40 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-[#5B9BD5]" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{item.name}</p>
                          <div className="flex gap-2 mt-1">
                            <Badge variant="outline" className="bg-sky-50 border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                              {item.venueName}
                            </Badge>
                            <div className="text-gray-900 text-sm font-mono font-medium">
                              {item.code}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteZiyarat(String(item.publicId))}
                          className="text-red-500 hover:bg-red-50 h-8 w-8 p-0"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        {/* <span className="text-xs text-gray-500">{item.createdOn}</span> */}
                        <div className="flex items-center gap-1.5">
                          <Switch
                            checked={item.isActive}
                            onCheckedChange={() => toggleStatus(item)}
                            className="data-[state=checked]:bg-[#5B9BD5]"
                          />
                          <span className={`text-xs font-medium ${item.isActive ? 'text-green-700' : 'text-gray-600'}`}>
                            {item.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(item)}
                          className="h-8 text-xs"
                        >
                          <Edit2 className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}

          </div>
          {/* PAGINATION COMPONENT FOR DRAFT */}
          <Pagination
            totalItems={ziyarattotalCount}
            itemsPerPage={filters.pageSize}
            currentPage={filters.pageNo}
            onPageChange={(page) =>
              setFilters((prev) => ({
                ...prev,
                pageNo: page, // convert back to 0-based
              }))
            }
            onPageSizeChange={(size) =>
              setFilters((prev) => ({
                ...prev,
                pageSize: size,
                pageNo: 1, // IMPORTANT: reset page
              }))
            }
            label=""
          />
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="bg-white sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle style={{ color: '#5B9BD5' }}>Add Ziyarat</DialogTitle>
            <DialogDescription>
              Create a new ziyarat entry
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <InputFieldset
                label="Name"
                required
                id="placeName"
                value={formData.placeName}
                onChange={(e) => {
                  setFormData({ ...formData, placeName: e.target.value });
                  setErrors({ ...errors, placeName: "" });
                }}
                placeholder="e.g., Haram of Maulana Ali ibn Abi Talib SA"
                className="text-sm"
              />
              {errors.placeName && (
                <p className="text-red-500 text-xs mt-1">{errors.placeName}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Code"
                  required
                  id="code"
                  value={formData.code}
                  onChange={(e) => {
                    setFormData({ ...formData, code: e.target.value });
                    setErrors({ ...errors, code: "" });
                  }}
                  placeholder="e.g., Z001"
                  className="text-sm"
                />
                {errors.code && (
                  <p className="text-red-500 text-xs mt-1">{errors.code}</p>
                )}
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Venue" required>
                  <Select
                    value={formData.venueName}
                    onValueChange={(value) => {
                      setFormData({ ...formData, venueName: value });
                      setErrors({ ...errors, venueName: "" });
                    }}
                  >
                    <SelectTriggerUI className="text-sm">
                      <SelectValue placeholder="Select venue" />
                    </SelectTriggerUI>
                    <SelectContent>
                      {venuesOptions.map((venue) => (
                        <SelectItem key={venue.venueId} value={venue.venueId.toString()}>
                          {venue.venueName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {errors.venueName && (
                  <p className="text-red-500 text-xs mt-1">{errors.venueName}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <SelectFieldset label="Status">
                <Select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onValueChange={(value) => setFormData({ ...formData, isActive: value === 'active' })}
                >
                  <SelectTriggerUI className="text-sm">
                    <SelectValue />
                  </SelectTriggerUI>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </SelectFieldset>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd} style={{ background: '#5B9BD5' }} className="text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Place
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-white sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle style={{ color: '#5B9BD5' }}>Edit Ziyarat</DialogTitle>
            <DialogDescription>
              Update ziyarat information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <InputFieldset
                label="Name"
                required
                id="edit-placeName"
                value={editFormData.placeName}
                onChange={(e) => {
                  setEditFormData({ ...editFormData, placeName: e.target.value });
                  setEditErrors({ ...editErrors, placeName: "" });
                }}
                placeholder="e.g., Haram of Maulana Ali ibn Abi Talib SA"
                className="text-sm"
              />
              {editErrors.placeName && (
                <p className="text-red-500 text-xs mt-1">{editErrors.placeName}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Code"
                  required
                  id="code"
                  value={editFormData.code}
                  onChange={(e) => {
                    setEditFormData({ ...editFormData, code: e.target.value });
                    setEditErrors({ ...editErrors, code: "" });
                  }}
                  placeholder="e.g., Z001"
                  className="text-sm"
                />
                {editErrors.code && (
                  <p className="text-red-500 text-xs mt-1">{editErrors.code}</p>
                )}
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Venue" required>
                  <Select
                    value={editFormData.venueName}
                    onValueChange={(value) => {
                      setEditFormData({ ...editFormData, venueName: value });
                      setEditErrors({ ...editErrors, venueName: "" });
                    }}
                  >
                    <SelectTriggerUI className="text-sm">
                      <SelectValue placeholder="Select venue" />
                    </SelectTriggerUI>
                    <SelectContent>
                      {venuesOptions.map((venue) => (
                        <SelectItem key={venue.venueId} value={venue.venueId.toString()}>
                          {venue.venueName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
              {editErrors.venueName && (
                <p className="text-red-500 text-xs mt-1">{editErrors.venueName}</p>
              )}
            </div>

            <div className="space-y-2">
              <SelectFieldset label="Status">
                <Select
                  value={editFormData.isActive ? 'active' : 'inactive'}
                  onValueChange={(value) => setEditFormData({ ...editFormData, isActive: value === 'active' })}
                >
                  <SelectTriggerUI className="text-sm">
                    <SelectValue />
                  </SelectTriggerUI>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </SelectFieldset>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} style={{ background: '#5B9BD5' }} className="text-white">
              <Edit2 className="w-4 h-4 mr-2" />
              Update Place
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* STATUS CHANGE CONFIRMATION POPUP */}
      <ConfirmDialog
        open={isStatusDialogOpen}
        onOpenChange={setIsStatusDialogOpen}
        title="Confirm Status Change"
        description={`Are you sure you want to ${selectedItem?.isActive ? "deactivate" : "activate"
          } this place?`}
        confirmText="Yes, Confirm"
        onConfirm={confirmStatusChange}
        onCancel={cancelStatusChange}
      />

      {/* DELETE CONFIRMATION POPUP */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Confirm Delete"
        description="Are you sure you want to delete?"
        confirmText="Yes, Delete"
        // loading={isDeleting}
        onConfirm={confirmDeleteDraft}
        onCancel={cancelDeleteDraft} // ✅ NOW IT WORKS
      >
      </ConfirmDialog>
    </div>
  );
};
